// TIP-488 (MV Stage 2) — the ONE reusable "Become a Member" page, mounted at a
// stable route (/membership/:companyId). Wraps PaymentScreen (the existing
// Stripe Embedded Checkout UI, reframed Verified → Membership) so profile CTAs,
// the dashboard, and the post-verify step can all route here.
import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PaymentScreen from "../../components/public/VerifyAccount/PaymentScreen";
import { useGetIndividualQuery } from "../../store/Company/companyApiSlice";
import { isOwnerRole } from "../../components/public/Company/Profile/GetMembershipButton";

const Membership = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { token, userInfo } = useSelector((state) => state.auth);

  // TIP-650 — only the company's owner (Claimer / Admin) can buy membership,
  // so tell PaymentScreen whether this visitor is one. Same query the TIP-647
  // picker reads for "my companies".
  const individualID = userInfo?.individual?.[0]?.individualID;
  const { data, isLoading, isFetching, isError } = useGetIndividualQuery(
    { individualID, token },
    { skip: !token || !individualID }
  );
  const viewerOwnsCompany = useMemo(() => {
    // No individual record → the user can't own anything yet.
    if (!individualID) return false;
    // Fail open: a failed lookup must not tell a real owner their business
    // belongs to someone else. The server-side guard is TIP-660.
    if (isError) return undefined;
    // A skipped / in-flight query has no data — "not answered yet", which is
    // not the same as "owns nothing".
    if (!data || isLoading || isFetching) return null;
    return (data.individual || []).some(
      (entry) => entry?.companyID?.companyID === companyId && isOwnerRole(entry?.role)
    );
  }, [individualID, isError, data, isLoading, isFetching, companyId]);

  // No lead/token here — PaymentScreen runs the direct company_id checkout path.
  // "Skip" simply returns to the dashboard (this isn't the gated verify flow).
  return (
    <PaymentScreen
      companyId={companyId}
      viewerOwnsCompany={viewerOwnsCompany}
      onSkip={() => navigate("/dashboard")}
    />
  );
};

export default Membership;
